import { backend, canApplyDate, forceGQLArray, isNotNullish } from "Backend";
import {
  BulkCaseClientStatusUpdateDocument,
  Case,
  CaseClientStatus,
  CustomPropertyQueryInput,
  CustomPropertyType,
  GetCasesParamsInput,
  GetLensCasesQueryVariables,
  NamedUser,
  SectionReviewState,
  SectionValueMatchMode,
} from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { CustomPropertyValue, serializeCustomPropertyValue } from "helpers/customProperties";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import triggerDownload from "helpers/triggerDownload";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  CaseFilterQueryParam,
  CaseFilterQueryParams,
  parseCaseFilterQueryParams,
  selectClientLensAssignees,
  selectClientLensCases,
  selectClientLensCasesStatus,
  selectClientLensCasesTotal,
} from "redux/slices/clientLens";
import RequestStatus from "redux/slices/RequestStatus";

function filterKeyHasValue([key, value]: [
  string,
  any[] | undefined | null | SectionValueMatchMode | boolean | number | string | CustomPropertyQueryInput,
]) {
  if ((Array.isArray(value) && value.length === 0) || value === undefined) return false;
  if (key === "sectionMatchMode" && value === SectionValueMatchMode.Any) return false;
  return true;
}

function getCasesParamsInputToCaseFilterQueryParam(key: keyof GetCasesParamsInput): string {
  switch (key) {
    case "assigneeId":
      return CaseFilterQueryParam.ASSIGNEE_ID;
    case "caseStatus":
      return CaseFilterQueryParam.CASE_STATUS;
    case "reviewStatus":
      return CaseFilterQueryParam.REVIEW_STATUS;
    case "textQuery":
      return CaseFilterQueryParam.SEARCH;
    case "plaintiffFirmId":
      return CaseFilterQueryParam.FIRM_ID;
    case "reviewStatusReason":
      return CaseFilterQueryParam.REVIEW_STATUS_REASON;
    case "sectionAssigneeIds":
    case "sectionsUnassignedOnly":
      return CaseFilterQueryParam.SECTION_ASSIGNEES;
    case "sectionReviewStates":
      return CaseFilterQueryParam.SECTION_STATUS;
    case "sectionIds":
      return CaseFilterQueryParam.SECTION_ID;
    case "minPageCount":
    case "maxPageCount":
      return CaseFilterQueryParam.HAS_DOCUMENTS;
    case "sectionMatchMode":
      return CaseFilterQueryParam.SECTION_MATCH_MODE;
    default:
      return "";
  }
}

export type UseCases = {
  resetFilters: () => void;
  addFilter: (key: string, value: string | string[] | undefined) => void;
  addPropFilter: (propertyKey: string, dataType: CustomPropertyType, value: CustomPropertyValue) => void;
  addCalcFilter: (propertyKey: string, dataType: CustomPropertyType, value: CustomPropertyValue) => void;
  handleCaseDownload: () => Promise<void>;
  bulkCaseClientStatusUpdate: (status: CaseClientStatus) => Promise<void>;
  filtersApplied: number;
  nonDefaultFiltersApplied: number;
  caseFilterParams: CaseFilterQueryParams;
  casesLoading: boolean;
  cases: Case[];
  optionsForUserAutocomplete: NamedUser[];
  selectedNamedUsers: NamedUser[];
  includePropertiesCheckbox: boolean;
  setIncludePropertiesCheckbox: Dispatch<SetStateAction<boolean>>;
  casesTotal: number;
  onPageChange: (page: number) => void;
  onCasesPerPageChange: (casesPerPage: string) => void;
  assignees: NamedUser[];
  getLensCasesQueryVariables: GetLensCasesQueryVariables;
  getCasesParamsInput: GetCasesParamsInput;
  selectedCases: Case[];
  setSelectedCases: Dispatch<SetStateAction<Case[]>>;
  selectAllCases: boolean;
  setSelectAllCases: Dispatch<SetStateAction<boolean>>;
  deselectAllCases: () => void;
  numCases: number;
};

export function useCases(lensId: number, staticAssignees: number[], reviewer: boolean): UseCases {
  const [includePropertiesCheckbox, setIncludePropertiesCheckbox] = useState<boolean>(false);
  const cases = useAppSelector(selectClientLensCases);
  const casesTotal = useAppSelector(selectClientLensCasesTotal);
  const casesStatus = useAppSelector(selectClientLensCasesStatus);
  const assignees = useAppSelector(selectClientLensAssignees);
  const { searchParams, updateParams } = useSearchParamsWithUpdate();
  const caseFilterParams = useMemo(
    () =>
      parseCaseFilterQueryParams(
        searchParams,
        true,
        reviewer ? [SectionReviewState.Review, SectionReviewState.Validation] : []
      ),
    [searchParams]
  );
  const casesLoading = casesStatus === RequestStatus.PENDING;
  const caseStatusForBackend = caseFilterParams.caseStatus === "all" ? undefined : caseFilterParams.caseStatus;
  const unassignedUser = {
    id: -1,
    firstName: "Unassigned",
    lastName: "",
    email: "",
  };
  const optionsForUserAutocomplete = [unassignedUser].concat(assignees);
  const selectedNamedUsers = optionsForUserAutocomplete.filter((u) =>
    caseFilterParams.sectionAssigneeIds.includes(u.id)
  );
  const [selectedCases, setSelectedCases] = useState<Case[]>([]);
  const [selectAllCases, setSelectAllCases] = useState(false);
  const numCases = selectAllCases ? casesTotal : selectedCases.length;

  const getLensCasesQueryVariables: GetLensCasesQueryVariables = {
    lensId,
    textQuery: caseFilterParams.textQuery,
    assigneeId: caseFilterParams.unassigned ? undefined : caseFilterParams.assigneeId,
    caseStatus: caseStatusForBackend,
    reviewStatuses: caseFilterParams.reviewStatuses,
    plaintiffFirmId: caseFilterParams.plaintiffFirmId,
    reviewStatusReason: caseFilterParams.reviewStatusReason,
    offset: caseFilterParams.page * caseFilterParams.rowsPerPage,
    limit: caseFilterParams.rowsPerPage,
    sectionReviewStates: caseFilterParams.sectionStatuses,
    sectionIds: caseFilterParams.sectionIds,
    sectionAssigneeIds:
      staticAssignees.length > 0
        ? staticAssignees
        : caseFilterParams.sectionsUnassignedOnly
          ? []
          : caseFilterParams.sectionAssigneeIds,
    sectionsUnassignedOnly: caseFilterParams.sectionsUnassignedOnly,
    sectionMatchMode: caseFilterParams.sectionMatchMode,
    minPageCount: caseFilterParams.hasDocuments ? 1 : undefined,
    maxPageCount: caseFilterParams.hasDocuments === false ? 0 : undefined,
    caseCalcQueries: caseFilterParams.caseCalcs,
    casePropertyQueries: caseFilterParams.caseProperties,
    unassigned: caseFilterParams.unassigned,
  };
  const getCasesParamsInput: GetCasesParamsInput = {
    textQuery: getLensCasesQueryVariables.textQuery,
    assigneeId: getLensCasesQueryVariables.assigneeId,
    caseStatus: getLensCasesQueryVariables.caseStatus,
    caseIds: selectAllCases ? null : selectedCases.map((c) => c.id),
    plaintiffFirmId: getLensCasesQueryVariables.plaintiffFirmId,
    reviewStatusReason: getLensCasesQueryVariables.reviewStatusReason,
    offset: 0,
    limit: casesTotal,
    sectionsUnassignedOnly: getLensCasesQueryVariables.sectionsUnassignedOnly,
    sectionMatchMode: getLensCasesQueryVariables.sectionMatchMode,
    minPageCount: getLensCasesQueryVariables.minPageCount,
    maxPageCount: getLensCasesQueryVariables.maxPageCount,
    unassigned: getLensCasesQueryVariables.unassigned,
    sectionReviewStates: forceGQLArray(getLensCasesQueryVariables.sectionReviewStates).filter(isNotNullish),
    sectionIds: forceGQLArray(getLensCasesQueryVariables.sectionIds).filter(isNotNullish),
    sectionAssigneeIds: forceGQLArray(getLensCasesQueryVariables.sectionAssigneeIds).filter(isNotNullish),
    reviewStatuses: forceGQLArray(getLensCasesQueryVariables.reviewStatuses).filter(isNotNullish),
    caseCalcQueries: forceGQLArray(getLensCasesQueryVariables.caseCalcQueries).filter(isNotNullish),
    casePropertyQueries: forceGQLArray(getLensCasesQueryVariables.casePropertyQueries).filter(isNotNullish),
  };
  const defaultFilterKeysAndValues = reviewer
    ? new Map([
        ["caseStatus", CaseClientStatus.Active],
        ["sectionReviewStates", [SectionReviewState.Review, SectionReviewState.Validation].toString()],
        ["sectionAssigneeIds", staticAssignees.toString()],
      ])
    : new Map([["caseStatus", CaseClientStatus.Active]]);
  const nonFilterKeys = new Set(["lensId", "textQuery", "offset", "limit"]);
  const filterKeysAndValues = Object.entries(getLensCasesQueryVariables).filter(([k]) => !nonFilterKeys.has(k));
  const filterKeysWithValues = filterKeysAndValues.filter((entry) => filterKeyHasValue(entry));
  const filtersApplied = filterKeysWithValues.length;
  const nonDefaultFiltersApplied = filterKeysWithValues.filter(
    ([key, value]) => defaultFilterKeysAndValues.get(key) !== value?.toString()
  ).length;

  const handleCaseDownload = async () => {
    const { fileName, data } = await backend.downloadCaseList({
      lensId,
      includeProperties: includePropertiesCheckbox,
      filters: getCasesParamsInput,
    });
    triggerDownload(
      new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName
    );
  };

  const bulkCaseClientStatusUpdate = async (status: CaseClientStatus) => {
    await runGqlMutation(BulkCaseClientStatusUpdateDocument, { lensId, caseFilters: getCasesParamsInput, status });
  };

  const deselectAllCases = () => {
    setSelectAllCases(false);
    setSelectedCases([]);
  };

  const addFilter = (key: string, value: string | string[] | undefined) => {
    updateParams([
      { key, value },
      // always reset to page 1 when adding a filter in case the new filter reduces the number of pages
      { key: "page", value: "1" },
    ]);
    deselectAllCases();
  };

  const addPropFilter = (propertyKey: string, dataType: CustomPropertyType, value: CustomPropertyValue) => {
    if (dataType === CustomPropertyType.Date) {
      if (!canApplyDate(propertyKey, value as Date | undefined, searchParams)) return;
    }
    updateParams([
      { key: `prop-${propertyKey}`, value: serializeCustomPropertyValue(value) || "" },
      { key: "page", value: "1" },
    ]);
    deselectAllCases();
  };

  const addCalcFilter = (propertyKey: string, dataType: CustomPropertyType, value: CustomPropertyValue) => {
    if (dataType === CustomPropertyType.Date) {
      if (!canApplyDate(propertyKey, value as Date | undefined, searchParams)) return;
    }
    updateParams([
      { key: `calc-${propertyKey}`, value: serializeCustomPropertyValue(value) || "" },
      { key: "page", value: "1" },
    ]);
    deselectAllCases();
  };

  const onPageChange = (page: number) => {
    updateParams([
      {
        key: "page",
        value: `${page + 1}`,
      },
    ]);
  };

  const onCasesPerPageChange = (casesPerPage: string) => {
    updateParams([
      {
        key: "rowsPerPage",
        value: casesPerPage,
      },
      { key: "page", value: `1` },
    ]);
  };

  const resetFilters = () => {
    updateParams(
      filterKeysWithValues.map(([key]) => ({
        key: getCasesParamsInputToCaseFilterQueryParam(key as keyof GetCasesParamsInput),
        value: undefined,
      })),
      { preventScrollReset: true }
    );
    deselectAllCases();
  };

  return {
    resetFilters,
    addFilter,
    addPropFilter,
    addCalcFilter,
    handleCaseDownload,
    bulkCaseClientStatusUpdate,
    filtersApplied,
    nonDefaultFiltersApplied,
    caseFilterParams,
    casesLoading,
    cases,
    optionsForUserAutocomplete,
    selectedNamedUsers,
    includePropertiesCheckbox,
    setIncludePropertiesCheckbox,
    casesTotal,
    onPageChange,
    onCasesPerPageChange,
    assignees,
    getLensCasesQueryVariables,
    getCasesParamsInput,
    selectedCases,
    setSelectedCases,
    selectAllCases,
    setSelectAllCases,
    deselectAllCases,
    numCases,
  };
}
